import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useCookieNotice } from "@/hooks/useCookieNotice";

/**
 * Informational cookie notice (NOT a consent manager).
 *
 * AutoAds currently uses only essential cookies (session, auth, CSRF) which
 * under Polish law (art. 173 PT) and ePrivacy do not require opt-in consent.
 * This banner just informs the user; when they acknowledge, we store the
 * ack in localStorage and never show it again (until policy version bumps
 * or 12 months pass).
 *
 * If we ever introduce analytics/marketing cookies, replace this component
 * with a full consent manager that gates those trackers behind opt-in.
 */

const TRANSLATIONS = {
    pl: {
        title: "Używamy plików cookies",
        body: "Korzystamy wyłącznie z plików cookies niezbędnych do działania Serwisu (sesja, logowanie, bezpieczeństwo). Nie używamy cookies analitycznych, marketingowych ani profilujących.",
        learnMore: "Polityka Cookies",
        ack: "Rozumiem",
        close: "Zamknij",
    },
    en: {
        title: "We use cookies",
        body: "We only use cookies that are strictly necessary to run the Service (session, login, security). We do not use analytics, marketing, or profiling cookies.",
        learnMore: "Cookie Policy",
        ack: "Got it",
        close: "Close",
    },
    ua: {
        title: "Ми використовуємо cookies",
        body: "Використовуємо лише cookies, необхідні для роботи Сервісу (сесія, вхід, безпека). Аналітичні, маркетингові та профілюючі cookies не використовуємо.",
        learnMore: "Політика Cookies",
        ack: "Зрозуміло",
        close: "Закрити",
    },
};

const detectLang = (search) => {
    // 1. Explicit ?lang= from URL takes priority (so legal-page visitors see matching banner).
    const fromQuery = new URLSearchParams(search).get("lang");
    if (["pl", "en", "ua"].includes(fromQuery)) return fromQuery;

    // 2. Fall back to browser language.
    if (typeof navigator !== "undefined") {
        const nav = (navigator.language || "").toLowerCase();
        if (nav.startsWith("pl")) return "pl";
        if (nav.startsWith("uk") || nav.startsWith("ua")) return "ua";
        if (nav.startsWith("ru")) return "ua"; // closest available to RU speakers
    }

    // 3. Default — Polish (we are a Polish service).
    return "pl";
};

const CookieNotice = () => {
    const { isLoaded, isAcked, ack } = useCookieNotice();
    const location = useLocation();

    const t = useMemo(() => {
        const lang = detectLang(location.search);
        return TRANSLATIONS[lang] || TRANSLATIONS.pl;
    }, [location.search]);

    // Don't render anything until we know whether to show it — prevents flash.
    if (!isLoaded || isAcked) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label={t.title}
            data-testid="cookie-notice"
            className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:max-w-md z-[100] animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
            <div className="relative rounded-xl border border-border bg-surface/95 backdrop-blur-md shadow-2xl shadow-black/40 p-4 sm:p-5">
                <button
                    type="button"
                    onClick={ack}
                    aria-label={t.close}
                    className="absolute top-2 right-2 p-1.5 rounded-md text-faint-c hover:text-text-primary hover:bg-surface-hover transition"
                    data-testid="cookie-notice-close"
                >
                    <X size={14} />
                </button>

                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-primary-c/10 flex items-center justify-center">
                        <Cookie size={18} className="text-primary-c" />
                    </div>

                    <div className="flex-1 min-w-0 pr-5">
                        <p className="font-display font-semibold text-sm text-text-primary mb-1">
                            {t.title}
                        </p>
                        <p className="text-xs text-secondary-c leading-relaxed">
                            {t.body}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={ack}
                                className="px-4 py-1.5 rounded-md bg-primary-c text-white text-xs font-medium hover:bg-primary-c/90 transition focus:outline-none focus:ring-2 focus:ring-primary-c/40"
                                data-testid="cookie-notice-ack"
                            >
                                {t.ack}
                            </button>
                            <Link
                                to="/cookies"
                                className="px-3 py-1.5 text-xs text-secondary-c hover:text-text-primary transition underline-offset-2 hover:underline"
                                data-testid="cookie-notice-learn-more"
                            >
                                {t.learnMore} →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieNotice;
